<template>
  <div v-if="hasValidBrand"
       class="brand-logo">
    <img class="logo"
         :class="logoClass"
         :alt="`${displayedBrand.description} Logo`"
         :src="useIcon ? displayedBrand.iconImage : displayedBrand.image">
    <div v-if="isReferralLogo"
         class="text-18">
      {{ $t('global.referral_rewards') }}
    </div>
  </div>
</template>

<script>
// Images
import Cash1Logo from '../../../assets/svg/Cash1Logo.svg'
import CaliforniaCheckCashLogo from '../../../assets/svg/CaliforniaCheckCashLogo.svg'
import CheckIntoCashLogo from '../../../assets/svg/CheckIntoCashLogo.svg'
import CheckSmartLogo from '../../../assets/svg/CheckSmartLogo.svg'
import CommunityChoiceLogo from '../../../assets/svg/CommunityChoiceLogo.svg'
import EasyMoneyLogo from '../../../assets/svg/EasyMoneyLogo.svg'
import InstaLoanLogo from '../../../assets/svg/InstaLoanLogo.svg'
import InstaLoanLogoContrast from '../../../assets/png/InstaLoanLogoContrast.png'
import TitleBucksLogo from '../../../assets/svg/TitleBucksLogo.svg'
import TitleMaxLogo from '../../../assets/svg/TitleMaxLogo.svg'

import c1lIcon from '../../../assets/png/c1lIcon.png'
import cccIcon from '../../../assets/png/cccIcon.png'
import cicIcon from '../../../assets/png/cicIcon.png'
import chsIcon from '../../../assets/png/chsIcon.png'
import ccfIcon from '../../../assets/png/ccfIcon.png'
import esmIcon from '../../../assets/png/esmIcon.png'
import inlIcon from '../../../assets/png/inlIcon.png'
import tbsIcon from '../../../assets/png/tbsIcon.png'
import tmxIcon from '../../../assets/png/tmxIcon.png'

// Utilities
import isEmpty from '../../../utility/isEmpty'
import router from '../../../utility/routerUtil'

import { AppBrandDisplayNameEnum, BrandEnum } from '../../../constants.js'

export default {
  name: 'brand-logo',

  props: {
    brand: {
      type: Number,
      default: undefined
    },

    useIcon: {
      type: Boolean,
      default: false
    },

    contrastingLogo: {
      type: Boolean,
      default: false
    },

    isReferralLogo: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    /**
     * Determines if brand is not empty.
     */
    hasValidBrand () {
      return !isEmpty(this.displayedBrand)
    },

    /**
     * The brand enum, defaults to route brand if not passed in explicitly.
     */
    brandToUse () {
      return isEmpty(this.brand) ? router.getCurrentRouteBrandEnum(this.$route) : this.brand
    },

    /**
     * The brand logo to display, for both contrasting and regular.
     */
    displayedBrand () {
      return this.contrastingLogo ? this.contrastBrandOptions[this.brandToUse] : this.brandOptions[this.brandToUse]
    },

    /**
     * If the brand logo to display is for CheckIntoCash
     */
    isCicLogo () {
      return this.brandToUse === BrandEnum.CheckIntoCash
    },

    /**
     * CSS class to add for referral logo or for CIC, default no class added.
     */
    logoClass () {
      return this.isCicLogo ? 'logo--cic' : this.isReferralLogo ? 'logo--referral' : ''
    },

    /**
     * The possible brand logos for each enum value
     */
    brandOptions () {
      return {
        [BrandEnum.Cash1Loans]: {
          elementId: 'cash1LoansLogo',
          image: Cash1Logo,
          iconImage: c1lIcon,
          description: AppBrandDisplayNameEnum.c1l
        },

        [BrandEnum.CaliforniaCheckCash]: {
          elementId: 'californiaCheckCashLogo',
          image: CaliforniaCheckCashLogo,
          iconImage: cccIcon,
          description: AppBrandDisplayNameEnum.ccc
        },

        [BrandEnum.CheckIntoCash]: {
          elementId: 'checkIntoCashLogo',
          image: CheckIntoCashLogo,
          iconImage: cicIcon,
          description: AppBrandDisplayNameEnum.cic
        },

        [BrandEnum.CheckSmart]: {
          elementId: 'checkSmartLogo',
          image: CheckSmartLogo,
          iconImage: chsIcon,
          description: AppBrandDisplayNameEnum.chs
        },

        [BrandEnum.CommunityChoice]: {
          elementId: 'communityChoiceLogo',
          image: CommunityChoiceLogo,
          iconImage: ccfIcon,
          description: AppBrandDisplayNameEnum.ccf
        },

        [BrandEnum.EasyMoney]: {
          elementId: 'easyMoneyLogo',
          image: EasyMoneyLogo,
          iconImage: esmIcon,
          description: AppBrandDisplayNameEnum.esm
        },

        [BrandEnum.InstaLoan]: {
          elementId: 'instaLoanLogo',
          image: InstaLoanLogo,
          iconImage: inlIcon,
          description: AppBrandDisplayNameEnum.inl
        },

        [BrandEnum.TitleBucks]: {
          elementId: 'titleBucksLogo',
          image: TitleBucksLogo,
          iconImage: tbsIcon,
          description: AppBrandDisplayNameEnum.tbs
        },

        [BrandEnum.None]: {},
        [BrandEnum.TitleMax]: {
          elementId: 'titleMaxLogo',
          image: TitleMaxLogo,
          iconImage: tmxIcon,
          description: AppBrandDisplayNameEnum.tmx
        }
      }
    },

    contrastBrandOptions () {
      return {
        [BrandEnum.None]: {},
        [BrandEnum.Cash1Loans]: {},
        [BrandEnum.CaliforniaCheckCash]: {},
        [BrandEnum.CommunityChoice]: {},
        [BrandEnum.CheckIntoCash]: {},
        [BrandEnum.CheckSmart]: {},
        [BrandEnum.EasyMoney]: {},
        [BrandEnum.InstaLoan]: {
          elementId: 'instaLoanLogo',
          image: InstaLoanLogoContrast,
          description: 'InstaLoan'
        },

        [BrandEnum.TitleBucks]: {},
        [BrandEnum.TitleMax]: {}
      }
    }
  }
}
</script>

<style scoped>
.brand-logo {
  text-align: center;
}

.logo {
  width: 100%;
  max-width: 9.38rem;
  max-height: 2.5rem;
}

.logo--referral {
  max-width: 14.5rem;
}

.logo--cic {
  max-width: 5rem;
}
</style>
